import React, { useState, useEffect } from 'react';
import { User, Plus, Edit, Trash2, Search, Filter } from 'lucide-react';

export const Characters = () => {
  const [characters, setCharacters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    // Mock data - replace with actual API call
    setTimeout(() => {
      setCharacters([
        {
          id: 1,
          name: 'Character 1',
          avatar: 'https://cdn.discordapp.com/embed/avatars/0.png',
          posts: 150,
          brackets: ['[', ']'],
          description: 'A helpful character for roleplaying',
          birthday: '2023-01-15',
          group: 'Main Characters',
          lastActive: '2 hours ago'
        },
        {
          id: 2,
          name: 'Character 2',
          avatar: 'https://cdn.discordapp.com/embed/avatars/1.png',
          posts: 89,
          brackets: ['{', '}'],
          description: 'Another character with different brackets',
          birthday: '2023-02-20',
          group: 'Side Characters',
          lastActive: '1 day ago'
        }
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  const filteredCharacters = characters.filter(character =>
    character.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    character.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-discord-blurple"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Characters</h1>
        <button 
          onClick={() => setShowAddModal(true)}
          className="discord-button flex items-center space-x-1"
        >
          <Plus className="w-4 h-4" />
          <span>Add Character</span>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="discord-card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-discord-light w-4 h-4" />
            <input
              type="text"
              placeholder="Search characters..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="discord-input w-full pl-10"
            />
          </div>
          <button className="discord-button flex items-center space-x-1">
            <Filter className="w-4 h-4" />
            <span>Filter</span>
          </button>
        </div>
      </div>

      {/* Characters Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCharacters.map((character) => (
          <div key={character.id} className="discord-card">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <img 
                  src={character.avatar}
                  alt={character.name}
                  className="w-16 h-16 rounded-full"
                />
                <div>
                  <h3 className="font-semibold text-white">{character.name}</h3>
                  <p className="text-sm text-discord-light">#{character.id}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-1">
                <button className="p-1 text-discord-light hover:text-white transition-colors">
                  <Edit className="w-4 h-4" />
                </button>
                <button className="p-1 text-discord-light hover:text-discord-red transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-discord-light">Brackets:</span>
                <span className="text-white font-mono">
                  {character.brackets[0]}text{character.brackets[1]}
                </span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-discord-light">Posts:</span>
                <span className="text-white">{character.posts}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-discord-light">Group:</span>
                <span className="text-white">{character.group}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-discord-light">Last Active:</span>
                <span className="text-white">{character.lastActive}</span>
              </div>
            </div>

            {character.description && (
              <div className="mt-4 pt-4 border-t border-discord-light/10">
                <p className="text-sm text-discord-light line-clamp-2">
                  {character.description}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredCharacters.length === 0 && (
        <div className="discord-card text-center py-12">
          <User className="w-16 h-16 text-discord-light/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">
            {searchTerm ? 'No characters found' : 'No characters yet'}
          </h3>
          <p className="text-discord-light">
            {searchTerm 
              ? 'Try adjusting your search terms'
              : 'Create your first character to get started'
            }
          </p>
        </div>
      )}

      {/* Add Character Modal (placeholder) */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="discord-card max-w-md w-full">
            <h2 className="text-xl font-semibold text-white mb-4">Add New Character</h2>
            <p className="text-discord-light mb-6">
              Character creation form will go here. This would connect to your bot's API.
            </p>
            <div className="flex justify-end space-x-3">
              <button 
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-discord-light hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button className="discord-button">
                Create Character
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
